import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SearchPicker } from "@/components/SearchPicker";
import { api, errorMessage } from "@/lib/api";
import { PER_PAGE, Pager } from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { PortalLinkSheet } from "@/components/PortalLinkSheet";

const BLANK = {
  full_name: "",
  firm_id: "",
  client_id: "",
  attach: "firm",
  designation: "",
  email: "",
  whatsapp: "",
  phone: "",
  is_direct_client: false,
  address: "",
  notes: "",
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isOwner = user?.role !== "clerk";
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [contactRes, firmRes, clientRes] = await Promise.all([
        api.get("/contacts"),
        api.get("/firms"),
        api.get("/clients"),
      ]);
      setContacts(contactRes.data);
      setFirms(firmRes.data);
      setClients(clientRes.data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load contacts"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return contacts.filter((c) => {
      const inScope =
        filter === "all"
          ? true
          : filter === "direct"
            ? c.is_direct_client
            : c.firm_id === filter || c.client_id === filter;
      if (!inScope) return false;
      if (!needle) return true;
      return [c.full_name, c.email, c.designation, c.firm_name, c.client_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [contacts, filter, query]);

  useEffect(() => {
    setPage(1);
  }, [filter, query]);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const grouped = useMemo(() => {
    const rows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const groups = new Map();
    rows.forEach((c) => {
      const key = c.firm_id || c.client_id || (c.is_direct_client ? "direct" : "unassigned");
      const label =
        c.firm_name ||
        (c.client_name ? `${c.client_name} (client)` : "") ||
        (c.is_direct_client ? "Direct clients" : "Not attached to a firm or client");
      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key).items.push(c);
    });
    return [...groups.entries()];
  }, [filtered, page]);

  const noMatch = filtered.length === 0 && contacts.length > 0;

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (contact) => {
    setEditing(contact);
    setForm({
      ...BLANK,
      ...contact,
      firm_id: contact.firm_id || "",
      client_id: contact.client_id || "",
      attach: contact.firm_id ? "firm" : contact.client_id ? "client" : "none",
    });
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("Full name is required");
      return;
    }
    setSaving(true);
    const payload = {
      full_name: form.full_name,
      firm_id: form.attach === "firm" ? form.firm_id || null : null,
      client_id: form.attach === "client" ? form.client_id || null : null,
      designation: form.designation,
      email: form.email,
      whatsapp: form.whatsapp,
      phone: form.phone,
      is_direct_client: form.attach === "none",
      address: form.address,
      notes: form.notes,
    };
    try {
      if (editing) await api.put(`/contacts/${editing.contact_id}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editing ? "Contact updated" : "Contact added");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save contact"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contact) => {
    if (!window.confirm(`Delete ${contact.full_name}?`)) return;
    try {
      await api.delete(`/contacts/${contact.contact_id}`);
      toast.success("Contact deleted");
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete contact"));
    }
  };

  const field = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div className="space-y-8" data-testid="contacts-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Who receives the fee note</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Instructing contacts
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            The people you correspond with — a solicitor from an instructing firm, someone from the
            client&rsquo;s own office, or a litigant who briefs you directly.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contact persons…"
            className="w-full rounded-sm sm:w-56"
            data-testid="contacts-search"
          />
          <div className="w-full sm:w-56">
            <SearchPicker
              items={[
                { id: "all", label: "All contacts" },
                { id: "direct", label: "Independent only" },
                ...firms.map((f) => ({ id: f.firm_id, label: f.name, hint: "Law firm" })),
                ...clients.map((c) => ({ id: c.client_id, label: c.name, hint: "Client" })),
              ]}
              value={filter}
              onSelect={(v) => setFilter(v || "all")}
              placeholder="All contacts"
              testId="contact-filter"
              emptyLabel="No firm or client matches that text."
            />
          </div>
          <button
            type="button"
            onClick={openNew}
            data-testid="add-contact-button"
            className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add contact
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="contacts-empty">
          <Users className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">No instructing contacts yet.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="contacts-list">
          {grouped.map(([key, group]) => (
            <section key={key}>
              <div className="flex items-baseline gap-3 border-b border-slate-200 pb-3">
                <h2 className="font-serif text-lg text-slate-900">{group.label}</h2>
                <span className="label-caps">{group.items.length}</span>
              </div>
              <div className="divide-y divide-slate-100 border-x border-b border-slate-200 bg-white">
                {group.items.map((contact) => (
                  <div
                    key={contact.contact_id}
                    className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`contact-row-${contact.contact_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-slate-900">
                        {contact.full_name}
                      </p>
                      <p className="text-[12px] text-slate-500">
                        {contact.designation || "—"} · {contact.case_count} matter(s)
                      </p>
                    </div>
                    <div className="flex flex-1 flex-col text-[12px] text-slate-600 sm:items-end">
                      <span>{contact.email || "No email on record"}</span>
                      <span className="num text-slate-500">
                        {contact.whatsapp || "No WhatsApp number"}
                      </span>
                    </div>
                    <div className="flex gap-1 sm:ml-6">
                      <PortalLinkSheet
                        scope="contact"
                        id={contact.contact_id}
                        name={contact.full_name}
                      />
                      <button
                        type="button"
                        onClick={() => openEdit(contact)}
                        data-testid={`edit-contact-${contact.contact_id}`}
                        className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </button>
                      {isOwner && (
                      <button
                        type="button"
                        onClick={() => remove(contact)}
                        data-testid={`delete-contact-${contact.contact_id}`}
                        className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {noMatch && (
            <p className="text-sm text-slate-500" data-testid="contacts-no-match">
              Nobody matches &ldquo;{query.trim()}&rdquo;.
            </p>
          )}
          <Pager page={page} total={filtered.length} onPage={setPage} testId="contacts-pager" />
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">
              {editing ? "Edit contact" : "Add instructing contact"}
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              Invoices are addressed to this person, at this email and WhatsApp number.
            </SheetDescription>
          </SheetHeader>
          <form className="mt-7 space-y-5" onSubmit={save} data-testid="contact-form">
            <div className="space-y-2">
              <Label className="label-caps">Full name *</Label>
              <Input {...field("full_name")} data-testid="contact-name-input" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Attached to</Label>
              <Select
                value={form.attach}
                onValueChange={(v) => setForm((f) => ({ ...f, attach: v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="contact-attach-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="firm">An instructing law firm</SelectItem>
                  <SelectItem value="client">A client</SelectItem>
                  <SelectItem value="none">Neither — stands on their own</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.attach === "firm" && (
              <div className="space-y-2">
                <Label className="label-caps">Law firm</Label>
                <SearchPicker
                  items={firms.map((f) => ({ id: f.firm_id, label: f.name, hint: f.city || "" }))}
                  value={form.firm_id}
                  onSelect={(v) => setForm((f) => ({ ...f, firm_id: v }))}
                  placeholder="Start typing the firm's name…"
                  testId="contact-firm"
                  allowNone
                  noneLabel="No firm"
                  emptyLabel="No firm matches that text."
                />
              </div>
            )}
            {form.attach === "client" && (
              <div className="space-y-2">
                <Label className="label-caps">Client</Label>
                <SearchPicker
                  items={clients.map((c) => ({
                    id: c.client_id,
                    label: c.name,
                    hint: c.city || "",
                  }))}
                  value={form.client_id}
                  onSelect={(v) => setForm((f) => ({ ...f, client_id: v }))}
                  placeholder="Start typing the client's name…"
                  testId="contact-client"
                  allowNone
                  noneLabel="No client"
                  emptyLabel="No client matches that text."
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-caps">Designation</Label>
              <Input
                {...field("designation")}
                placeholder="Partner, Senior Associate…"
                data-testid="contact-designation-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Direct email</Label>
              <Input {...field("email")} type="email" data-testid="contact-email-input" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">WhatsApp mobile (with country code)</Label>
              <Input
                {...field("whatsapp")}
                placeholder="+91 98200 11234"
                data-testid="contact-whatsapp-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Telephone</Label>
              <Input
                {...field("phone")}
                placeholder="022 2200 1234"
                data-testid="contact-phone-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Address</Label>
              <Textarea {...field("address")} rows={2} data-testid="contact-address-input" className="rounded-sm" />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-contact-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? "Save changes" : "Add contact"}
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
